import React from "react";
import { Link } from "react-router-dom";

// Openbare help-/handleidingpagina voor partner-organisaties.
// Bereikbaar zonder inloggen via /help.
const styles = {
  page: {
    background: "#f5f6fa",
    minHeight: "100vh",
    padding: "0 0 60px 0",
    color: "#32384a",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    lineHeight: 1.6,
  },
  topbar: {
    background: "#ffffff",
    borderBottom: "1px solid #e6e8ef",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  logo: { height: "38px" },
  container: {
    maxWidth: "860px",
    margin: "0 auto",
    padding: "0 20px",
  },
  hero: {
    background: "linear-gradient(135deg, #4b6cb7 0%, #182848 100%)",
    color: "#fff",
    borderRadius: "12px",
    padding: "34px 32px",
    margin: "28px 0",
    boxShadow: "0 8px 24px rgba(24,40,72,0.18)",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    padding: "26px 30px",
    marginBottom: "22px",
    boxShadow: "0 2px 10px rgba(50,56,74,0.06)",
    border: "1px solid #eceef4",
  },
  h2: {
    fontSize: "1.35rem",
    margin: "0 0 4px 0",
    color: "#182848",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  stepNum: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "30px",
    height: "30px",
    borderRadius: "50%",
    background: "#4b6cb7",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.95rem",
  },
  ol: { paddingLeft: "22px", margin: "12px 0" },
  li: { marginBottom: "8px" },
  callout: {
    background: "#fff8e1",
    border: "1px solid #ffe082",
    borderLeft: "5px solid #ffb300",
    borderRadius: "8px",
    padding: "18px 22px",
    margin: "26px 0",
  },
  calloutTitle: { fontWeight: 700, color: "#9a6b00", marginBottom: "6px" },
  mail: { color: "#b26a00", fontWeight: 700, textDecoration: "none" },
  toc: {
    background: "#fff",
    borderRadius: "12px",
    padding: "18px 24px",
    marginBottom: "22px",
    border: "1px solid #eceef4",
  },
  tocLink: { color: "#4b6cb7", textDecoration: "none" },
  tag: {
    display: "inline-block",
    background: "#eef1fb",
    color: "#4b6cb7",
    borderRadius: "6px",
    padding: "2px 8px",
    fontSize: "0.85rem",
    fontWeight: 600,
    margin: "0 4px",
  },
  footer: {
    textAlign: "center",
    color: "#8a90a2",
    fontSize: "0.9rem",
    marginTop: "30px",
  },
  loginBtn: {
    display: "inline-block",
    background: "#fff",
    color: "#182848",
    fontWeight: 700,
    textDecoration: "none",
    padding: "10px 22px",
    borderRadius: "8px",
    marginTop: "16px",
  },
};

const HelpPage = () => {
  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <img src="/brand/logo.png" alt="Logo" style={styles.logo} />
        <strong>Handleiding partner-organisaties</strong>
      </div>

      <div style={styles.container}>
        <div style={styles.hero}>
          <h1 style={{ margin: "0 0 8px 0", fontSize: "2rem" }}>
            Evenementen beheren
          </h1>
          <p style={{ margin: 0, opacity: 0.92 }}>
            Op deze pagina leest u als partner-organisatie stap voor stap hoe u
            inlogt en hoe u een evenement toevoegt of wijzigt op het
            VVV&nbsp;Epe-platform. U hoeft hiervoor niet ingelogd te zijn — deze
            pagina is openbaar.
          </p>
          <a href="/login" style={styles.loginBtn}>
            → Naar de inlogpagina
          </a>
        </div>

        {/* Inhoud */}
        <div style={styles.toc}>
          <strong>Inhoud</strong>
          <ol style={styles.ol}>
            <li style={styles.li}>
              <a href="#inloggen" style={styles.tocLink}>Inloggen op het platform</a>
            </li>
            <li style={styles.li}>
              <a href="#wachtwoord" style={styles.tocLink}>Wachtwoord instellen of wijzigen</a>
            </li>
            <li style={styles.li}>
              <a href="#terugvinden" style={styles.tocLink}>Uw evenementen terugvinden</a>
            </li>
            <li style={styles.li}>
              <a href="#toevoegen" style={styles.tocLink}>Een nieuw evenement toevoegen</a>
            </li>
            <li style={styles.li}>
              <a href="#wijzigen" style={styles.tocLink}>Een bestaand evenement wijzigen</a>
            </li>
            <li style={styles.li}>
              <a href="#publiceren" style={styles.tocLink}>Publiceren: wie zet het online?</a>
            </li>
            <li style={styles.li}>
              <a href="#uitgelicht" style={styles.tocLink}>Uitgelicht evenement</a>
            </li>
            <li style={styles.li}>
              <a href="#nietgekoppeld" style={styles.tocLink}>Uw evenement staat er nog niet tussen?</a>
            </li>
          </ol>
        </div>

        {/* 1. Inloggen */}
        <div style={styles.card} id="inloggen">
          <h2 style={styles.h2}>
            <span style={styles.stepNum}>1</span> Inloggen op het platform
          </h2>
          <ol style={styles.ol}>
            <li style={styles.li}>
              Ga naar de inlogpagina: <a href="/login">https://backoffice.vvvepe.net/login</a>.
            </li>
            <li style={styles.li}>
              Vul uw <strong>e-mailadres</strong> en <strong>wachtwoord</strong> in en
              klik op <strong>Inloggen</strong>.
            </li>
            <li style={styles.li}>
              Als partner-organisatie logt u <strong>direct</strong> in met uw
              e-mailadres en wachtwoord. U hoeft <strong>geen sms-code</strong> in te
              vullen.
            </li>
            <li style={styles.li}>
              Na het inloggen komt u op het startscherm. Aan de linkerkant vindt u het
              menu waarmee u naar uw evenementen gaat (zie stap 3).
            </li>
            <li style={styles.li}>
              Wachtwoord vergeten of wijzigen? Zie <strong>stap 2</strong> hieronder.
            </li>
          </ol>
          <p style={{ margin: "10px 0 0", color: "#6b7280" }}>
            Nog geen inloggegevens ontvangen? Neem contact op via{" "}
            <a href="mailto:info@woutervanreeven.nl" style={styles.mail}>info@woutervanreeven.nl</a>.
          </p>
        </div>

        {/* 2. Wachtwoord */}
        <div style={styles.card} id="wachtwoord">
          <h2 style={styles.h2}>
            <span style={styles.stepNum}>2</span> Wachtwoord instellen of wijzigen
          </h2>
          <p>
            Bent u uw wachtwoord vergeten, of wilt u het om een andere reden wijzigen?
            Dat regelt u zelf via de inlogpagina:
          </p>
          <ol style={styles.ol}>
            <li style={styles.li}>
              Klik op de inlogpagina op <strong>Wachtwoord vergeten</strong>.
            </li>
            <li style={styles.li}>
              Vul uw <strong>e-mailadres</strong> in en verstuur het formulier. U
              ontvangt een e-mail met een link om een nieuw wachtwoord te kiezen.
            </li>
            <li style={styles.li}>
              Klik op de link in de e-mail. Voer uw <strong>nieuwe wachtwoord</strong>{" "}
              twee keer in en klik op <strong>Verzenden</strong>.
            </li>
            <li style={styles.li}>
              Uw nieuwe wachtwoord moet voldoen aan deze eisen:
              <ul style={{ marginTop: "8px" }}>
                <li>minimaal <strong>8 tekens</strong>;</li>
                <li>minstens één <strong>kleine letter</strong>;</li>
                <li>minstens één <strong>hoofdletter</strong>;</li>
                <li>minstens één <strong>cijfer</strong>.</li>
              </ul>
              Voldoet het wachtwoord niet, dan ziet u meteen een melding met wat er nog
              ontbreekt.
            </li>
            <li style={styles.li}>
              Na het opslaan kunt u direct met uw nieuwe wachtwoord inloggen.
            </li>
          </ol>
          <p style={{ margin: "10px 0 0", color: "#6b7280" }}>
            Werkt de link niet meer? Deze is een beperkte tijd geldig. Vraag in dat geval
            via <strong>Wachtwoord vergeten</strong> een nieuwe e-mail aan.
          </p>
        </div>

        {/* 3. Terugvinden */}
        <div style={styles.card} id="terugvinden">
          <h2 style={styles.h2}>
            <span style={styles.stepNum}>3</span> Uw evenementen terugvinden
          </h2>
          <ol style={styles.ol}>
            <li style={styles.li}>
              Klik na het inloggen in het menu aan de linkerkant op{" "}
              <span style={styles.tag}>VVV Items</span>.
            </li>
            <li style={styles.li}>
              Klik nogmaals op <span style={styles.tag}>VVV Items</span> om het
              overzicht met evenementen te openen.
            </li>
            <li style={styles.li}>
              Gebruik het <strong>zoekveld</strong> (minimaal 3 tekens) om snel een
              bepaald evenement te vinden. In de statuskolom ziet u of een evenement{" "}
              <strong>Online</strong>, <strong>Offline</strong> of{" "}
              <strong>Nieuwe update</strong> is.
            </li>
          </ol>
        </div>

        {/* 3. Toevoegen */}
        <div style={styles.card} id="toevoegen">
          <h2 style={styles.h2}>
            <span style={styles.stepNum}>4</span> Een nieuw evenement toevoegen
          </h2>
          <ol style={styles.ol}>
            <li style={styles.li}>
              Klik in het menu op <span style={styles.tag}>VVV Items Toevoegen</span>{" "}
              (of op de knop <strong>Items Toevoegen</strong> boven het overzicht).
            </li>
            <li style={styles.li}>
              U doorloopt nu een aantal tabbladen. Vul ze van links naar rechts in en
              klik onderaan telkens op <strong>Opslaan / Volgende</strong>:
              <ul style={{ marginTop: "8px" }}>
                <li><strong>Wat</strong> – titel, korte omschrijving en uitgebreide tekst van het evenement.</li>
                <li><strong>Waar</strong> – de locatie van het evenement.</li>
                <li><strong>Wanneer</strong> – datum, begin- en eindtijd. U kunt ook een herhaling instellen (bv. wekelijks).</li>
                <li><strong>Media</strong> – voeg één of meer foto's toe.</li>
                <li><strong>Eigenschappen</strong> – kenmerken en categorieën (bijvoorbeeld voor kinderen, toegankelijkheid, enz.).</li>
              </ul>
            </li>
            <li style={styles.li}>
              Als partner-organisatie staat het type automatisch op{" "}
              <strong>Evenement</strong> en wordt uw eigen locatie al ingevuld.
            </li>
            <li style={styles.li}>
              Klaar met invullen? Uw evenement is opgeslagen. VVV&nbsp;Epe controleert
              het en zet het daarna online (zie stap 6).
            </li>
          </ol>
        </div>

        {/* 4. Wijzigen */}
        <div style={styles.card} id="wijzigen">
          <h2 style={styles.h2}>
            <span style={styles.stepNum}>5</span> Een bestaand evenement wijzigen
          </h2>
          <ol style={styles.ol}>
            <li style={styles.li}>
              Open het overzicht via <span style={styles.tag}>VVV Items</span> en zoek
              het evenement op.
            </li>
            <li style={styles.li}>
              Klik op de rij van het evenement of op de knop{" "}
              <strong>Wijzigen</strong>.
            </li>
            <li style={styles.li}>
              Pas de gegevens aan in de tabbladen{" "}
              <strong>Wat</strong>, <strong>Waar</strong>, <strong>Wanneer</strong>,{" "}
              <strong>Media</strong>, <strong>Contact</strong> en{" "}
              <strong>Eigenschappen</strong>. Klik op elk tabblad op{" "}
              <strong>Opslaan</strong> om uw wijziging te bewaren.
            </li>
            <li style={styles.li}>
              Uw wijzigingen worden bewaard. Zodra u iets wijzigt aan een reeds online
              evenement, controleert VVV&nbsp;Epe de update en publiceert deze opnieuw.
            </li>
          </ol>
        </div>

        {/* 5. Publiceren */}
        <div style={styles.card} id="publiceren">
          <h2 style={styles.h2}>
            <span style={styles.stepNum}>6</span> Publiceren: wie zet het online?
          </h2>
          <p>
            Als partner-organisatie <strong>vult u de gegevens in en slaat u ze op</strong>,
            maar u zet een evenement <strong>niet zelf online</strong>. VVV&nbsp;Epe
            controleert uw evenement en publiceert het. Bij een evenement dat al online
            staat ziet u de melding{" "}
            <em>“U kunt items niet publiceren of unpubliceren”</em> — dat is normaal.
          </p>
          <p style={{ marginBottom: 0 }}>
            Duurt het lang voordat uw evenement online staat, of klopt er iets niet aan
            de publicatie? Neem dan contact op via{" "}
            <a href="mailto:info@woutervanreeven.nl" style={styles.mail}>info@woutervanreeven.nl</a>.
          </p>
        </div>

        {/* 7. Uitgelicht */}
        <div style={styles.card} id="uitgelicht">
          <h2 style={styles.h2}>
            <span style={styles.stepNum}>7</span> Uitgelicht evenement
          </h2>
          <p>
            Een <strong>uitgelicht evenement</strong> krijgt extra aandacht op externe
            sites, zoals <strong>wildetenopdeveluwe.nl</strong>. VVV&nbsp;Epe zet dit aan
            via het tabblad <strong>Eigenschappen</strong> → schakelaar{" "}
            <em>Uitgelicht evenement</em>. Het wordt zichtbaar zodra het evenement
            (opnieuw) online is gezet.
          </p>
          <p style={{ marginBottom: 0 }}>
            Wilt u uw evenement laten uitlichten? Mail de naam van het evenement naar{" "}
            <a href="mailto:info@woutervanreeven.nl?subject=Evenement%20uitlichten" style={styles.mail}>
              info@woutervanreeven.nl
            </a>.
          </p>
        </div>

        {/* 6. Niet gekoppeld */}
        <div style={styles.callout} id="nietgekoppeld">
          <div style={styles.calloutTitle}>
            ⚠️ Uw evenement staat er nog niet tussen?
          </div>
          <p style={{ margin: "0 0 8px" }}>
            Ziet u uw evenement niet in het overzicht, of is uw organisatie nog niet
            aan een evenement <strong>gekoppeld</strong>? Dan is de koppeling met uw
            partner-organisatie mogelijk nog niet gemaakt.
          </p>
          <p style={{ margin: 0 }}>
            Stuur in dat geval een e-mail naar{" "}
            <a href="mailto:info@woutervanreeven.nl?subject=Evenement%20koppelen%20aan%20mijn%20organisatie" style={styles.mail}>
              info@woutervanreeven.nl
            </a>{" "}
            met de naam van uw organisatie en het betreffende evenement. Wij koppelen
            het evenement dan aan uw account, zodat u het zelf kunt beheren.
          </p>
        </div>

        <div style={styles.footer}>
          Hulp nodig? Mail{" "}
          <a href="mailto:info@woutervanreeven.nl" style={styles.mail}>info@woutervanreeven.nl</a> ·{" "}
          <Link to="/login" style={styles.tocLink}>Inloggen</Link>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
